// AN INLINE GENERATIVE BLOCK of the assistant's thread (AI_ASSISTANT_SPEC v1
// §3; DESIGN-WL-ASSISTANT-MAIN-VIEW-1). The shared chat card dispatcher
// (shared/_simone/ChatCard.js) renders a card of type 'view' | 'records' |
// 'board' | 'widget' | 'form' | 'sources' through this component, in the sidebar AND in the main thread (there
// as a full-board-width row — components/AssistantMainThread.js).
//
// Anatomy: a head (the app's calm glyph for a records or widget block, the
// title, the actions that mean something for it: Open in split · Save as app ·
// Save as board · Refine), a body — the answer view mounted inline (bounded,
// scrolls inside), the records block, an installed app's widget (embed_widget:
// its own body with the card's params), or a board's summary — and a foot:
// where the block comes from, in the house micro-label (the home widget's
// footer). The card's surface comes from the dispatcher (the chat's posture);
// every value from functions/assistantThreadBlocks.js through `el.call`.
export const AssistantThreadBlock = {
  extends: 'CommonCard',
  tag: 'section',
  role: 'group',
  ariaLabel: (el, s) => el.call('assistantBlockTitle', s),
  dataBlockType: (el, s) => (s && s.type) || null,
  // A widget card waits for the installed apps before it can mount its body.
  ariaBusy: (el, s) => (el.call('assistantEmbedWidgetStatus', s) === 'loading' ? 'true' : null),
  flow: 'y',
  gap: 'Z',
  width: '100%',
  minWidth: '0',
  boxSizing: 'border-box',

  BlockHead: {
    flow: 'x',
    flexWrap: 'wrap',
    align: 'center space-between',
    gap: 'Z',
    minWidth: '0',

    // The app's calm glyph (the home App strip's tile): a records or widget
    // block only — a view or a board has no app of its own. `if`, not `hide`:
    // a hidden tile still builds its Icon, and an Icon whose name is empty is
    // read by its KEY — "[Icon] Unknown icon name 'Icon'" under every view and
    // board answer (dev DFI 2026-10-05, the chains lane). No glyph → no tile.
    // The name never goes empty: a tile that showed and then lost its app
    // (if → false keeps the element live) re-reads AppGlyphTile's own default.
    AppGlyphTile: {
      'aria-hidden': 'true',
      if: (el, s) => !!el.call('assistantBlockIcon', s),
      Icon: { name: (el, s) => el.call('assistantBlockIcon', s) || 'star' }
    },

    BlockTitles: {
      flow: 'y',
      gap: 'X',
      minWidth: '0',
      flex: '1 1 auto',
      BlockTitle: {
        tag: 'h3',
        margin: '0',
        fontSize: 'A',
        fontWeight: '500',
        lineHeight: '1.3',
        color: 'title',
        overflowWrap: 'anywhere',
        text: (el, s) => el.call('assistantBlockTitle', s)
      }
    },

    BlockActions: {
      flow: 'x',
      flexWrap: 'wrap',
      align: 'center',
      gap: 'Y',
      childProps: { compact: true },

      AppCapsuleBtnToolbar_open: {
        dataBlockOpen: true,
        hide: (el, s) => !el.call('assistantBlockCan', s, 'open'),
        text: '{{ workspace.assistantBlock.openInSplit | polyglot }}',
        onClick: (e, el, s) => el.call('assistantBlockOpen', s)
      },
      // Save as app — an answer view becomes an app; a BUILT app (Apps mode)
      // is installed, and reads "Added to workspace" once it is.
      AppCapsuleBtnToolbar_app: {
        dataBlockSaveApp: true,
        hide: (el, s) => !el.call('assistantBlockCan', s, 'saveApp'),
        subscribeTo: ['workspace'],
        disabled: (el, s) => (el.call('assistantBlockSaveAppState', s) !== 'save' ? true : null),
        text: (el, s) => {
          const state = el.call('assistantBlockSaveAppState', s)
          return el.call(
            'polyglot',
            state === 'added'
              ? 'workspace.addApp.install.added'
              : state === 'checking'
                ? 'workspace.addApp.install.checking'
                : 'workspace.assistantBlock.saveAsApp'
          )
        },
        onClick: (e, el, s) => el.call('assistantBlockSaveApp', s)
      },
      AppCapsuleBtnToolbar_board: {
        dataBlockSaveBoard: true,
        hide: (el, s) => !el.call('assistantBlockCan', s, 'saveBoard'),
        disabled: (el, s) => (el.call('assistantBlockSaveLabel', s && s.root, s) ? true : null),
        onClick: (e, el, s) => el.call('assistantBlockSaveBoard', el, s),
        AppLabelStack: {
          labels: [
            'workspace.assistantBoard.saveAsBoard',
            'workspace.assistantBoard.saving',
            'workspace.assistantBoard.saved'
          ],
          shown: (el, s) => el.call('assistantBlockSaveLabel', s && s.root, s)
        }
      },
      AppCapsuleBtnToolbar_refine: {
        dataBlockRefine: true,
        hide: (el, s) => !el.call('assistantBlockCan', s, 'refine'),
        text: '{{ workspace.assistantBlock.refine | polyglot }}',
        onClick: (e, el, s) => el.call('assistantBlockRefine', s)
      }
    }
  },

  // An answer view, inline: bounded, it scrolls inside — the thread never
  // grows a page tall. Mounted once per route (assistantBlockMountView).
  BlockView: {
    dataBlockView: true,
    hide: (el, s) => !s || s.type !== 'view' || el.call('assistantBlockViewGone', s),
    maxHeight: 'H',
    overflow: 'auto',
    minWidth: '0',
    round: 'radiusControl',
    onRender: (el, s) => el.call('assistantBlockMountView', el, s),
    onUpdate: (el, s) => el.call('assistantBlockMountView', el, s)
  },
  BlockViewGone: {
    tag: 'p',
    hide: (el, s) => !el.call('assistantBlockViewGone', s),
    margin: '0',
    fontSize: 'Z',
    color: 'caption',
    text: '{{ workspace.assistantBlock.viewGone | polyglot }}'
  },

  AssistantRecordsBlock: {
    hide: (el, s) => !s || s.type !== 'records'
  },

  // "What can you do here?" — the capabilities catalogue (functions/
  // aiCapabilities.js): Ask · Do · Build · Inspect, each row tappable.
  AssistantCapabilities: {
    hide: (el, s) => !s || s.type !== 'capabilities'
  },

  // An installed app's WIDGET (embed_widget): the app's own body mounted
  // inline with the card's params — the mount the home and the generated board
  // use (assistantEmbedWidgetMount → assistantBoardMountBody). The installed
  // apps publish after a cold load; the card re-mounts when they land
  // (`subscribeTo`). While they load: a skeleton; a widget not installed here:
  // one sentence.
  BlockWidget: {
    dataBlockWidget: true,
    hide: (el, s) => el.call('assistantEmbedWidgetStatus', s) !== 'ready',
    display: 'flex',
    flexDirection: 'column',
    minWidth: '0',
    minHeight: '0',
    subscribeTo: ['workspaceApps', 'workspaceProject'],
    onRender: (el, s) => el.call('assistantEmbedWidgetMount', el, s),
    onUpdate: (el, s) => el.call('assistantEmbedWidgetMount', el, s)
  },
  BlockWidgetSkeleton: {
    hide: (el, s) => el.call('assistantEmbedWidgetStatus', s) !== 'loading',
    flow: 'y',
    gap: 'Z',
    minWidth: '0',
    Skeleton_1: { width: '40%', height: 'B' },
    Skeleton_2: { minHeight: 'C', round: 'radiusControl' }
  },
  // Its app did not load here, or it is not installed here — one sentence.
  BlockWidgetNote: {
    tag: 'p',
    dataBlockWidgetNote: true,
    hide: (el, s) => !el.call('assistantEmbedWidgetNote', s),
    margin: '0',
    fontSize: 'Z',
    color: 'caption',
    overflowWrap: 'anywhere',
    text: (el, s) => el.call('assistantEmbedWidgetNote', s)
  },

  // An app's own FORM, prefilled from the assistant's proposal (U3,
  // functions/assistantAppForms.js): the proposal as labelled fields, ONE
  // action that opens the app's form (nothing is written until the member
  // saves there), and — once the app reports it — the saved row, which opens
  // the record. Re-reads when the request moves (`_aiForms`) or the apps
  // publish.
  BlockForm: {
    dataBlockForm: true,
    hide: (el, s) => el.call('assistantFormStatus', s) !== 'ready',
    flow: 'y',
    gap: 'Z',
    minWidth: '0',
    subscribeTo: ['_aiForms', 'workspaceApps', 'workspaceProject'],
    BlockFormFields: {
      tag: 'dl',
      margin: '0',
      flow: 'y',
      gap: 'Y',
      minWidth: '0',
      childExtends: 'AssistantFormField',
      childrenAs: 'state',
      children: (el, s) => el.call('assistantFormFields', s) || []
    },
    BlockFormSaved: {
      flow: 'y',
      gap: 'Y',
      minWidth: '0',
      hide: (el, s) => !(el.call('assistantFormSavedRows', s) || []).length,
      childExtends: 'HomeWidgetRow',
      childrenAs: 'state',
      children: (el, s) => el.call('assistantFormSavedRows', s) || []
    },
    BlockFormFoot: {
      flow: 'x',
      flexWrap: 'wrap',
      align: 'center flex-start',
      gap: 'Z',
      minWidth: '0',
      AppCapsuleBtnPrimary: {
        dataBlockFormOpen: true,
        compact: true,
        hide: (el, s) => !el.call('assistantFormAction', s),
        text: (el, s) => el.call('assistantFormAction', s),
        onClick: (e, el, s) => el.call('assistantFormOpen', s)
      },
      BlockFormStatus: {
        tag: 'p',
        dataBlockFormStatus: true,
        margin: '0',
        fontSize: 'Z',
        color: 'caption',
        overflowWrap: 'anywhere',
        ariaLive: 'polite',
        text: (el, s) => el.call('assistantFormStatusText', s)
      }
    }
  },
  // WEB SOURCES (U4, functions/assistantSources.js): search results as source
  // cards — each a real link to its page, in a new tab — and a fetched page as
  // a quote with its source.
  BlockSources: {
    dataBlockSources: true,
    hide: (el, s) => !s || s.type !== 'sources',
    flow: 'y',
    gap: 'Y',
    minWidth: '0',
    BlockQuote: {
      tag: 'figure',
      margin: '0',
      flow: 'y',
      gap: 'Y',
      minWidth: '0',
      hide: (el, s) => !el.call('assistantSourcesQuote', s),
      Quote: {
        tag: 'blockquote',
        margin: '0',
        padding: 'Z A',
        round: 'radiusControl',
        theme: 'layer',
        fontSize: 'Z1',
        lineHeight: '1.5',
        color: 'title',
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere',
        text: (el, s) => (el.call('assistantSourcesQuote', s) || {}).text || ''
      },
      Cite: {
        tag: 'figcaption',
        fontSize: 'Z',
        color: 'caption',
        Link: {
          dataSourceQuote: true,
          href: (el, s) => (el.call('assistantSourcesQuote', s) || {}).url || null,
          target: '_blank',
          rel: 'noopener noreferrer',
          color: 'caption',
          overflowWrap: 'anywhere',
          text: (el, s) => {
            const q = el.call('assistantSourcesQuote', s) || {}
            return q.title && q.title !== q.domain ? q.title + ' · ' + q.domain : q.domain || ''
          }
        }
      }
    },
    BlockSourceCards: {
      flow: 'y',
      gap: 'Y',
      minWidth: '0',
      childExtends: 'AssistantSourceCard',
      childrenAs: 'state',
      children: (el, s) => el.call('assistantSourcesRows', s) || []
    }
  },

  // A form this member may not use, or one not here: one sentence.
  BlockFormNote: {
    tag: 'p',
    dataBlockFormNote: true,
    hide: (el, s) => !el.call('assistantFormNote', s),
    margin: '0',
    fontSize: 'Z',
    color: 'caption',
    overflowWrap: 'anywhere',
    text: (el, s) => el.call('assistantFormNote', s)
  },

  BlockBoardSummary: {
    tag: 'p',
    hide: (el, s) => !s || s.type !== 'board' || !s.summary,
    margin: '0',
    fontSize: 'Z',
    color: 'caption',
    overflowWrap: 'anywhere',
    text: (el, s) => (s && s.summary) || ''
  },

  // The foot: where the block comes from ("From CRM · Deals"), the home
  // widget's footer voice — out of the head, so the head reads glyph + title.
  AppMicroLabel: {
    dataBlockSource: true,
    whiteSpace: 'normal',
    text: (el, s) => el.call('assistantBlockSource', s)
  }
}
